import type { BreadcrumbLink } from '@noths/polaris-client-ribbons-design-system';

import { buildTaxonomyHref } from 'src/adapters/storeToPageURL/facets/buildTaxonomyHref';
import type { TaxonomyEntry } from 'src/redux/content/types';
import { findActiveOption } from 'src/redux/productsQuery/helpers/facetOptionHelpers';
import type { TaxonomyContext } from 'src/redux/productsQuery/selectors/selectTaxonomyContext';
import { resolveTaxonomyHeading } from 'src/utils/resolveTaxonomyHeading';
import { normaliseForMatch } from 'src/utils/selectTaxonomyContentEntry';

export const HOMEPAGE_BREADCRUMB: BreadcrumbLink = { rel: '/', text: 'Homepage' };

/**
 * Builds the breadcrumb trail (Homepage + one entry per selected taxonomy level)
 * from the taxonomy context.
 *
 * The current-page (last) crumb is resolved through the same
 * `resolveTaxonomyHeading` used by the h1, so the two always match: the matched
 * Contentstack entry's authored heading when the entry is for this page, else
 * the deepest taxonomy label sentence-cased. The entry heading is only applied
 * when its URL matches the last crumb, so a parent-fallback entry (a page with
 * no content of its own) falls back to the deepest label rather than repeating
 * the parent's heading.
 */
export const adaptTaxonomyContextToBreadcrumbs = (
  context: TaxonomyContext,
  matchedEntry?: TaxonomyEntry | null,
): BreadcrumbLink[] => {
  const { facets, surface, taxonomyAttributeAliases, valuesPerLevel } = context;

  if (taxonomyAttributeAliases.length === 0) {
    return [HOMEPAGE_BREADCRUMB];
  }

  const links: BreadcrumbLink[] = [HOMEPAGE_BREADCRUMB];
  for (let i = 0; i < valuesPerLevel.length; i++) {
    // A breadcrumb trail is a single linear path: stop at the first level that
    // is skipped (no selection) or multi-selected.
    if (valuesPerLevel[i]!.length !== 1) {
      break;
    }
    const option = findActiveOption(facets, taxonomyAttributeAliases[i]!);
    if (!option) {
      break;
    }

    links.push({
      rel: buildTaxonomyHref({
        facets,
        levelCount: i + 1,
        surface,
        taxonomyAttributeAliases,
      }),
      text: option.title,
    });
  }

  const lastLink = links[links.length - 1];
  if (lastLink !== HOMEPAGE_BREADCRUMB && lastLink.rel) {
    const entryHeading = matchedEntry?.content?.heading;
    const currentPageHeading =
      entryHeading && normaliseForMatch(lastLink.rel) === normaliseForMatch(matchedEntry!.url)
        ? entryHeading
        : undefined;

    links[links.length - 1] = {
      ...lastLink,
      text:
        resolveTaxonomyHeading({ heading: currentPageHeading, taxonomyLabel: lastLink.text }) ??
        lastLink.text,
    };
  }

  return links;
};
